import type { DmMessage } from '../../../../../services/dmMessages';
import type { StagedAttachment } from './stagedAttachment.ts';

export interface OutgoingMessage {
    clientRequestId: string;
    patientId: string;
    text: string;
    attachment: StagedAttachment | null;
}

export type PendingStatus = 'sending' | 'failed';

export interface PendingMessage extends DmMessage {
    pending: true;
    status: PendingStatus;
    errorMessage: string | null;
    uploadPercent: number | null;
    outgoing: OutgoingMessage;
}

export type DisplayMessage = DmMessage | PendingMessage;

export function isPending(message: DisplayMessage): message is PendingMessage {
    return 'pending' in message && message.pending === true;
}

export function pendingIdFor(clientRequestId: string): string {
    return `pending-${clientRequestId}`;
}

export function toPendingMessage(
    outgoing: OutgoingMessage,
    senderName: string,
    now: Date
): PendingMessage {
    return {
        id: pendingIdFor(outgoing.clientRequestId),
        text: outgoing.text,
        sender: 'practice',
        senderName,
        createdAt: now.toISOString(),
        attachment: null,
        pending: true,
        status: 'sending',
        errorMessage: null,
        uploadPercent: null,
        outgoing
    };
}

function updateById(
    list: PendingMessage[],
    id: string,
    update: (message: PendingMessage) => PendingMessage
): PendingMessage[] {
    return list.map((message) => (message.id === id ? update(message) : message));
}

// A retry keeps the bubble's place and timestamp so the thread doesn't reorder.
export function upsertPending(list: PendingMessage[], next: PendingMessage): PendingMessage[] {
    if (!list.some((message) => message.id === next.id)) return [...list, next];
    return updateById(list, next.id, (message) => ({
        ...message,
        status: 'sending',
        errorMessage: null,
        uploadPercent: null
    }));
}

export function markPendingFailed(
    list: PendingMessage[],
    id: string,
    errorMessage: string
): PendingMessage[] {
    return updateById(list, id, (message) => ({
        ...message,
        status: 'failed',
        errorMessage,
        uploadPercent: null
    }));
}

export function setPendingProgress(
    list: PendingMessage[],
    id: string,
    uploadPercent: number
): PendingMessage[] {
    return updateById(list, id, (message) =>
        message.uploadPercent === uploadPercent ? message : { ...message, uploadPercent }
    );
}

export function removePending(list: PendingMessage[], id: string): PendingMessage[] {
    return list.filter((message) => message.id !== id);
}

// A message only ever goes to the patient it was written for, and one send runs at a time.
export function canSendFor(
    outgoing: OutgoingMessage,
    patientId: string,
    isSending: boolean
): boolean {
    return !isSending && outgoing.patientId === patientId;
}

export function pendingForPatient(list: PendingMessage[], patientId: string): PendingMessage[] {
    return list.filter((message) => message.outgoing.patientId === patientId);
}
